// NPM Dependencies
import React from 'react';

// Components
import Location from './BookingsData/listing/location';
import Action from './BookingsData/listing/action';
import DateData from './BookingsData/listing/dateData';
import BookingIdCell from './BookingsData/listing/bookingIdCell';

// Styles
import { LobIcons } from './BookingsData/listing/listingStyles';
import { GenericSpan, GenericP } from 'commonStyles';

// Constants
import { APPLICATION_CONSTANTS, FILTER_CONSTANTS, LOB_TYPES, MRB_CONSTANTS, ADOBE_EVENT_VALUES, SORT_KEY } from 'AppConstants';
import { ACCESS_TYPES } from 'accessControl/config';

// Utils
import { formatCurrency } from 'UtilityComponents/Util';

export const pageApiConfigs = {
	limit: MRB_CONSTANTS.PAGE_LIMIT,
	page: 1
};

export const SEARCH_SECTION_CONFIG = {
	heading: 'Manage Requests & Bookings',
	subHeading: 'Showing Results for',
	placeHolderTxt: 'Search for Traveller Name, Email ID or Booking ID',
	btnTxt: 'Search'
};

export const filterTypes = {
	'date': 'date',
	'location': 'location',
	'approver': 'approver',
	'traveller': 'traveller',
	'customLocation': 'customLocation'
};

export const MRB_FILTERS_CONSTANTS = {
	POLICY_TYPE: 'POLICY_TYPE',
	APPROVER: 'APPROVER',
	TRAVELLER: 'TRAVELLER',
	DATE_OF_REQUEST: 'DATE_OF_REQUEST',
	DATE_OF_TRAVEL: 'DATE_OF_TRAVEL',
	TRIP_TYPE: 'TRIP_TYPE',
	STAY_LOCATION: 'STAY_LOCATION',
	TRAVEL_LOCATIONS: 'TRAVEL_LOCATIONS',
	IN_POLICY: 'IN_POLICY',
	OUT_POLICY: 'OO_POLICY',
	FLIGHT_TYPE: 'FLIGHT',
	HOTEL_TYPE: 'HOTEL',
	CAB_TYPE: 'CABS',
	BUS_TYPE: 'BUS',
	TRAIN_TYPE: 'TRAIN',
	FLIGHT_MOD_TYPE: 'FLIGHT_MOD',
	REQUISTION_TYPE: 'REQUISITION',
	TRAVEL_AGE: 'TRAVEL_AGE',
	DATE_OF_BOOKING: 'DATE_OF_BOOKING'
};

export const ORG_ADMIN = {
	'id': 'ORG_ADMIN',
	'label': 'Organisation Admin',
	'name': 'Organisation Admin',
	'value': 'ADMIN'
};

export const MRB_FILTER_CONFIG = {
	[MRB_FILTERS_CONSTANTS.IN_POLICY]: {
		label: 'In Policy',
		value: 1,
		countKey: 'inPolicy'
	},
	[MRB_FILTERS_CONSTANTS.OUT_POLICY]: {
		label: 'Out of Policy',
		value: 0,
		countKey: 'outPolicy'
	},
	[MRB_FILTERS_CONSTANTS.FLIGHT_TYPE]: {
		label: 'Flight',
		value: MRB_FILTERS_CONSTANTS.FLIGHT_TYPE,
		countKey: MRB_FILTERS_CONSTANTS.FLIGHT_TYPE
	},
	[MRB_FILTERS_CONSTANTS.HOTEL_TYPE]: {
		label: 'Hotel',
		value: MRB_FILTERS_CONSTANTS.HOTEL_TYPE,
		countKey: MRB_FILTERS_CONSTANTS.HOTEL_TYPE
	},
	[MRB_FILTERS_CONSTANTS.CAB_TYPE]: {
		label: 'Cab',
		value: MRB_FILTERS_CONSTANTS.CAB_TYPE,
		countKey: MRB_FILTERS_CONSTANTS.CAB_TYPE
	},
	[MRB_FILTERS_CONSTANTS.BUS_TYPE]: {
		label: 'Bus',
		value: MRB_FILTERS_CONSTANTS.BUS_TYPE,
		countKey: MRB_FILTERS_CONSTANTS.BUS_TYPE
	},
	[MRB_FILTERS_CONSTANTS.REQUISTION_TYPE]: {
		label: 'Trip Requisition',
		value: MRB_FILTERS_CONSTANTS.REQUISTION_TYPE,
		countKey: MRB_FILTERS_CONSTANTS.REQUISTION_TYPE
	},
	[MRB_FILTERS_CONSTANTS.FLIGHT_MOD_TYPE]: {
		label: 'Flight Modification',
		value: MRB_FILTERS_CONSTANTS.FLIGHT_MOD_TYPE,
		countKey: MRB_FILTERS_CONSTANTS.FLIGHT_MOD_TYPE
	},
	[MRB_FILTERS_CONSTANTS.TRAIN_TYPE]: {
		label: 'Train',
		value: MRB_FILTERS_CONSTANTS.TRAIN_TYPE,
		countKey: MRB_FILTERS_CONSTANTS.TRAIN_TYPE
	}
};

const DATE_RANGE_LIMIT_MONTHS = 3;
const DATE_RANGE_HELPER_TEXT = `Please select a date range within ${DATE_RANGE_LIMIT_MONTHS} months`;

export const filters = {
	[MRB_FILTERS_CONSTANTS.POLICY_TYPE]: {
		label: 'Policy Type',
		type: FILTER_CONSTANTS.CHECKBOX,
		key: MRB_CONSTANTS.POLICY,
		filterType: MRB_CONSTANTS.POLICY,
		value: '',
		hideClear: false,
		singleSelect: false,
		selectedCount: 0,
		trackVal: '',
		options: [{
			value: 1,
			countKey: 'inPolicy',
			label: 'In Policy',
			checked: false,
			filterKey: MRB_CONSTANTS.POLICY,
			count: '',
			trackVal: '',
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_IN_POLICY,
			index: 0
		},
		{
			value: 0,
			countKey: 'outPolicy',
			label: 'Out of Policy',
			checked: false,
			filterKey: MRB_CONSTANTS.POLICY,
			count: '',
			trackVal: '',
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_OUT_OF_POLICY,
			index: 0
		}]
	},
	[MRB_FILTERS_CONSTANTS.APPROVER]: {
		label: 'Approver',
		key: MRB_CONSTANTS.APPROVER,
		filterType: filterTypes.approver,
		isGrouped: true,
		type: FILTER_CONSTANTS.DROPDOWN,
		isMulti: true,
		value: [],
		selectedCount: 0,
		dynamic: true,
		placeholder: 'Search by Approver Email ID',
		trackVal: '',
		options: [{ ...ORG_ADMIN }],
		hideIndicator: true,
		className: 'manageBookings'
	},
	[MRB_FILTERS_CONSTANTS.TRAVELLER]: {
		label: 'Traveller',
		key: MRB_FILTERS_CONSTANTS.TRAVELLER,
		filterType: filterTypes.traveller,
		isGrouped: true,
		type: FILTER_CONSTANTS.DROPDOWN,
		isMulti: false,
		value: '',
		selectedCount: 0,
		dynamic: true,
		placeholder: 'Search by Traveller Email ID',
		trackVal: '',
		options: [],
		hideIndicator: true,
		className: 'manageBookings'
	},
	[MRB_FILTERS_CONSTANTS.DATE_OF_REQUEST]: {
		label: 'Date of Request',
		type: FILTER_CONSTANTS.CHECKBOX,
		startKey: 'startDate',
		endKey: 'endDate',
		value: '',
		filterType: filterTypes.date,
		key: FILTER_CONSTANTS.DATE_FILTER,
		hideClear: false,
		selectedCount: 0,
		singleSelect: true,
		options: [{
			label: 'Your Dates',
			type: 'datePicker',
			value: '',
			trackVal: '',
			checked: false,
			text: 'selected dates',
			countKey: '',
			key: FILTER_CONSTANTS.DATE_FILTER,
			filterKey: FILTER_CONSTANTS.DATE_FILTER,
			disabledDays: {
				after: APPLICATION_CONSTANTS.TODAY
			},
			maxRangeMonths: DATE_RANGE_LIMIT_MONTHS,
			helperText: DATE_RANGE_HELPER_TEXT,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_DATE_REQ_CUSTOM
		}],
		trackVal: ''
	},
	[MRB_FILTERS_CONSTANTS.DATE_OF_BOOKING]: {
		label: 'Date of Booking',
		type: FILTER_CONSTANTS.CHECKBOX,
		startKey: 'startDate',
		endKey: 'endDate',
		value: '',
		filterType: filterTypes.date,
		key: FILTER_CONSTANTS.DATE_FILTER,
		hideClear: false,
		selectedCount: 0,
		singleSelect: true,
		options: [{
			label: 'Your Dates',
			type: 'datePicker',
			value: '',
			trackVal: '',
			checked: false,
			text: 'selected dates',
			countKey: '',
			key: FILTER_CONSTANTS.DATE_FILTER,
			filterKey: FILTER_CONSTANTS.DATE_FILTER,
			disabledDays: {
				after: APPLICATION_CONSTANTS.TODAY
			},
			maxRangeMonths: DATE_RANGE_LIMIT_MONTHS,
			helperText: DATE_RANGE_HELPER_TEXT,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_DATE_BOOKING_CUSTOM
		}]
	},
	[MRB_FILTERS_CONSTANTS.TRAVEL_AGE]: {
		label: 'Age of Request',
		type: FILTER_CONSTANTS.CHECKBOX,
		startKey: 'startHour',
		endKey: 'endHour',
		value: '',
		filterType: filterTypes.date,
		key: FILTER_CONSTANTS.DATE_FILTER,
		hideClear: false,
		selectedCount: 0,
		singleSelect: true,
		options: [{
			label: 'Less than 3 hours',
			start: 0,
			end: 2,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_AGE_REQ_LESS_THREE
		},
		{
			label: '3- 12 hours',
			start: 3,
			end: 12,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_AGE_REQ_THREE_TWELVE
		},
		{
			label: 'Greater than 12 hours',
			start: 13,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_AGE_REQ_MORE_TWELVE
		}]
	},
	[MRB_FILTERS_CONSTANTS.DATE_OF_TRAVEL]: {
		label: 'Date of Travel',
		type: FILTER_CONSTANTS.CHECKBOX,
		startKey: 'fromTravelDate',
		endKey: 'toTravelDate',
		value: '',
		filterType: filterTypes.date,
		key: FILTER_CONSTANTS.DATE_FILTER,
		hideClear: false,
		selectedCount: 0,
		singleSelect: true,
		options: [{
			label: 'Your Dates',
			type: 'datePicker',
			value: '',
			trackVal: '',
			checked: false,
			text: 'selected dates',
			countKey: '',
			key: FILTER_CONSTANTS.DATE_FILTER,
			filterKey: FILTER_CONSTANTS.DATE_FILTER,
			disabledDays: {
				before: APPLICATION_CONSTANTS.TODAY
			},
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_DATE_TRAVEL_CUSTOM
		}]
	},
	[MRB_FILTERS_CONSTANTS.TRIP_TYPE]: {
		label: 'Trip Type',
		type: FILTER_CONSTANTS.CHECKBOX,
		key: MRB_CONSTANTS.TRIP_TYPE,
		filterType: MRB_CONSTANTS.TRIP_TYPE,
		value: '',
		hideClear: false,
		singleSelect: false,
		selectedCount: 0,
		trackVal: '',
		options: [{
			...MRB_FILTER_CONFIG[MRB_FILTERS_CONSTANTS.FLIGHT_TYPE],
			checked: false,
			filterKey: MRB_CONSTANTS.TRIP_TYPE,
			count: 0,
			trackVal: '',
			index: 0,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_TRIP_TYPE_FLIGHT
		},
		{
			...MRB_FILTER_CONFIG[MRB_FILTERS_CONSTANTS.FLIGHT_MOD_TYPE],
			checked: false,
			filterKey: MRB_CONSTANTS.TRIP_TYPE,
			count: 0,
			trackVal: '',
			index: 1,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_TRIP_TYPE_FLIGHT_MOD
		},
		{
			...MRB_FILTER_CONFIG[MRB_FILTERS_CONSTANTS.HOTEL_TYPE],
			checked: false,
			filterKey: MRB_CONSTANTS.TRIP_TYPE,
			count: 0,
			trackVal: '',
			index: 2,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_TRIP_TYPE_HOTEL
		},
		{
			...MRB_FILTER_CONFIG[MRB_FILTERS_CONSTANTS.CAB_TYPE],
			checked: false,
			filterKey: MRB_CONSTANTS.TRIP_TYPE,
			count: 0,
			trackVal: '',
			index: 3,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_TRIP_TYPE_CAB
		},
		{
			...MRB_FILTER_CONFIG[MRB_FILTERS_CONSTANTS.BUS_TYPE],
			checked: false,
			filterKey: MRB_CONSTANTS.TRIP_TYPE,
			count: 0,
			trackVal: '',
			index: 4,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_TRIP_TYPE_BUS
		},
		{
			...MRB_FILTER_CONFIG[MRB_FILTERS_CONSTANTS.TRAIN_TYPE],
			checked: false,
			filterKey: MRB_CONSTANTS.TRIP_TYPE,
			count: 0,
			trackVal: '',
			index: 5,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_TRIP_TYPE_TRAIN
		},
		{
			...MRB_FILTER_CONFIG[MRB_FILTERS_CONSTANTS.REQUISTION_TYPE],
			checked: false,
			filterKey: MRB_CONSTANTS.TRIP_TYPE,
			count: 0,
			trackVal: '',
			index: 6,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_TRIP_TYPE_TRF
		}
		]
	}
};

export const destinationField = {
	name: '',
	label: '',
	type: 'text',
	outLabel: true,
	customClass: 'col-12',
	label: 'Stay Location',
	key: 'location',
	filterType: 'location',
	isGrouped: true,
	value: '',
	selectedCount: 0,
	dynamic: true,
	noBlurChange: true,
	placeHolder: 'Search by property city',
	singleSelect: true,
	trackVal: '',
	options: []
};

export const fromDestinationField = {
	name: 'source',
	label: '',
	type: 'text',
	customClass: 'col-6',
	key: 'location',
	filterType: 'customLocation',
	value: '',
	dynamic: true,
	noBlurChange: true,
	placeHolder: 'From',
	singleSelect: true,
	selectedCount: 0,
	trackVal: ''
};
export const toDestinationField = {
	name: 'destination',
	label: '',
	type: 'text',
	customClass: 'col-6',
	label: '',
	key: 'location',
	filterType: 'customLocation',
	value: '',
	dynamic: true,
	noBlurChange: true,
	placeHolder: 'To',
	singleSelect: true,
	trackVal: ''
};

export const customLocation = {
	key: 'location',
	filterType: 'customLocation',
	options: [fromDestinationField, toDestinationField]
};

export const dateFilterConfig = {
	value: '',
	alwaysEnable: true,
	countKey: '',
	label: '',
	count: '',
	filterKey: FILTER_CONSTANTS.DATE_FILTER,
	start: '',
	end: '',
	index: '',
	checked: false,
	trackVal: ''
};

export const TRAVEL_REQUEST_CONFIG = {
	[MRB_FILTERS_CONSTANTS.DATE_OF_REQUEST]: [
		{
			label: 'Today',
			startDay: 0,
			endDay: 0,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_DATE_REQ_TODAY
		},
		{
			label: 'Yesterday',
			startDay: 1,
			endDay: 1,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_DATE_REQ_YESTERDAY
		},
		{
			label: 'Last 7 days',
			startDay: 7,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_DATE_REQ_LAST_SEVEN
		}
	],
	[MRB_FILTERS_CONSTANTS.DATE_OF_BOOKING]: [
		{
			label: 'Last 7 days',
			startDay: 7,
			endDay: 0,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_DATE_BOOKING_LAST_SEVEN
		},
		{
			label: 'Last 30 days',
			startDay: 30,
			endDay: 0,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_DATE_BOOKING_LAST_THIRTY
		}
	],
	[MRB_FILTERS_CONSTANTS.DATE_OF_TRAVEL]: [
		{
			label: 'Today',
			startDay: 0,
			endDay: 0,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_DATE_TRAVEL_TODAY
		},
		{
			label: 'Tomorrow',
			startDay: 1,
			endDay: 1,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_DATE_TRAVEL_TOMORROW
		},
		{
			label: 'Next Seven Days',
			endDay: 7,
			adobeVal: ADOBE_EVENT_VALUES.MANAGE_BOOKING.FILTER_DATE_TRAVEL_NEXT_SEVEN
		}
	]
};

export const DATE_OF_TRAVEL_CONFIG = [{
	label: 'Tomorrow',
	startDay: 1,
	endDay: 1
},
{
	label: 'Next Seven Days',
	endDay: 7
}
];

export const DEFAULT_SORT_BY_TYPE = {
	[MRB_CONSTANTS.PENDING_REQUEST_TYPE]: { key: 'AGE', direction: SORT_KEY.DESCENDING },
	[MRB_CONSTANTS.EXPIRED_REQUEST_TYPE]: { key: 'AGE', direction: SORT_KEY.DESCENDING },
	[MRB_CONSTANTS.UPCOMING_REQUEST_TYPE]: { key: 'BOOKING_DATE', direction: SORT_KEY.DESCENDING }
};

export const REQUEST_TYPE_ADOBE_PREFIX = {
	[MRB_CONSTANTS.PENDING_REQUEST_TYPE]: ADOBE_EVENT_VALUES.MANAGE_BOOKING.PENDING_REQUEST_PREFIX,
	[MRB_CONSTANTS.EXPIRED_REQUEST_TYPE]: ADOBE_EVENT_VALUES.MANAGE_BOOKING.EXPIRED_REQUESTS_CLICK,
	[MRB_CONSTANTS.UPCOMING_REQUEST_TYPE]: ADOBE_EVENT_VALUES.MANAGE_BOOKING.ALL_BOOKINGS_CLICK
};

export const TabList = [
	{
		key: MRB_CONSTANTS.PENDING_REQUEST_TYPE,
		value: 'PENDING REQUESTS',
		count: ''
	},
	{
		key: MRB_CONSTANTS.EXPIRED_REQUEST_TYPE,
		value: 'EXPIRED REQUESTS',
		count: ''
	},
	{
		key: MRB_CONSTANTS.UPCOMING_REQUEST_TYPE,
		value: 'ALL BOOKINGS',
		count: ''
	}
];

export const TABLE_PROPS = {
	container: {
		classes: { root: 'report_table' }
	},
	table: {
		stickyHeader: true
	},
	bodyRowChild: {
		classes: {
			root: 'report_table'
		}
	}
};

export const ICON_MAP = {
	[LOB_TYPES.FLIGHT]: 'flt',
	[LOB_TYPES.HOTEL]: 'htl',
	[LOB_TYPES.BUS]: 'ic_bus',
	[LOB_TYPES.CABS]: 'ic_cab',
	[LOB_TYPES.REQUISITION]: 'ic_requisition',
	[LOB_TYPES.TRAIN]: 'ic_train',
	[LOB_TYPES.INTL_FLIGHT]: 'flt',
	[LOB_TYPES.INTL_HOTEL]: 'htl'
};

export const FLIGHT_TYPE_TITLE_MAP = {
	O: 'One Way',
	R: 'Round Trip',
	M: 'Multi City'
};

export const BOOKING_TITLE_MAP = {
	[LOB_TYPES.FLIGHT]: 'Flight',
	[LOB_TYPES.HOTEL]: 'Hotel',
	[LOB_TYPES.CABS]: 'Cab',
	[LOB_TYPES.BUS]: 'Bus',
	[LOB_TYPES.TRAIN]: 'Train',
	[LOB_TYPES.REQUISITION]: 'Trip Requisition'
};

export const coachmarkTextConf = {
	list: {
		heading: 'View & Download Data',
		subHeading: 'View summarised information from the selected report and download the required information.'
	},
	filter: {
		heading: 'View Filter Data',
		subHeading: 'View summarised information from the selected report and download the required information.'
	},
	help: {
		heading: 'Refer to Help Section',
		subHeading: 'Revisit the benefits and onboarding process of Report from the Help section here.'
	}
};

const PENDING_REQUEST_COLUMNS = [
	{
		header: '',
		accessor: 'tripType',
		headerCellProps: {
			width: '30px',
			padding: '8px 0'
		},
		bodyCellProps: {
			padding: '10px 0',
			noBdr: true
		},
		formatter: (value) => <LobIcons icon={value} />
	},
	{
		header: 'TRIP DETAILS',
		accessor: 'location',
		expandTrigger: true,
		headerCellProps: {
			width: '145px',
			padding: '8px 8px 8px 0'
		},
		bodyCellProps: {
			padding: '10px 8px 10px 0',
			noBdr: true
		},
		formatter: (value) => <Location {...value} />
	},
	{
		header: 'TRAVELLER',
		accessor: 'traveller',
		headerCellProps: {
			width: '133px',
			padding: '8px 8px 8px 0'
		},
		bodyCellProps: {
			padding: '10px 8px 10px 0',
			noBdr: true
		},
		formatter: (value) => (
			<GenericSpan>
				<GenericP fsize='14px' $lHeight='20px' color='#000'>{value?.name}</GenericP>
				{value?.email && <GenericP fsize='12px' $lHeight='16px' color='#4a4a4a'>{value.email}</GenericP>}
			</GenericSpan>
		)
	},
	{
		header: 'APPROVER',
		accessor: 'approver',
		headerCellProps: {
			width: '121px',
			padding: '8px 8px 8px 0'
		},
		bodyCellProps: {
			padding: '10px 8px 10px 0',
			noBdr: true
		},
		formatter: (value) => (
			<GenericSpan>
				<GenericP fsize='14px' $lHeight='20px' color='#000'>{value?.name}</GenericP>
				{value?.email && <GenericP fsize='12px' $lHeight='16px' color='#4a4a4a'>{value.email}</GenericP>}
			</GenericSpan>
		)
	},
	{
		header: 'DATE OF TRAVEL',
		accessor: 'travelDateData',
		key: 'DATE_OF_TRAVEL',
		isSort: true,
		headerCellProps: {
			width: '117px',
			padding: '8px 8px 8px 0'
		},
		bodyCellProps: {
			padding: '10px 8px 10px 0',
			noBdr: true
		},
		formatter: (value) => <DateData {...value} />
	},
	{
		header: 'AGE OF REQUEST',
		accessor: 'formattedAge',
		key: 'AGE',
		isSort: true,
		headerCellProps: {
			width: '92px',
			padding: '8px 8px 8px 0'
		},
		bodyCellProps: {
			padding: '10px 8px 10px 0',
			noBdr: true
		},
		formatter: (value) => value || '-'
	},
	{
		header: 'EXPENSE',
		accessor: 'estimatedCost',
		key: 'ESTIMATED_COST',
		isSort: true,
		headerCellProps: {
			width: '90px',
			padding: '8px 8px 8px 0'
		},
		bodyCellProps: {
			padding: '10px 8px 10px 0',
			noBdr: true
		},
		formatter: (value) => (value ? formatCurrency(value, true) : '-')
	},
	{
		header: 'ACTIONS',
		key: 'actions',
		accessor: 'action',
		headerCellProps: {
			width: '100px',
			padding: '8px 0'
		},
		bodyCellProps: {
			padding: '10px 0',
			noBdr: true
		},
		formatter: (value) => <Action {...value} type={MRB_CONSTANTS.PENDING_REQUEST_TYPE} />
	}
];

const EXPIRED_COLUMNS = PENDING_REQUEST_COLUMNS.map((column) => (
	column.key === 'actions' ?
		{ ...column, formatter: (value) => <Action {...value} type={MRB_CONSTANTS.EXPIRED_REQUEST_TYPE} /> } :
		column
));

export const TABLE_COLUMNS = {
	[MRB_CONSTANTS.PENDING_REQUEST_TYPE]: PENDING_REQUEST_COLUMNS,
	[MRB_CONSTANTS.EXPIRED_REQUEST_TYPE]: EXPIRED_COLUMNS,
	[MRB_CONSTANTS.UPCOMING_REQUEST_TYPE]: [
		{
			header: '',
			accessor: 'tripType',
			headerCellProps: {
				width: '30px',
				padding: '8px 0'
			},
			bodyCellProps: {
				padding: '10px 0',
				noBdr: true
			},
			formatter: (value) => <LobIcons icon={value} />
		},
		{
			header: 'BOOKING ID',
			accessor: 'bookingId',
			noExpand: true,
			headerCellProps: {
				width: '123px',
				padding: '8px 8px 8px 0'
			},
			bodyCellProps: {
				padding: '10px 8px 10px 0',
				noBdr: true
			},
			formatter: (value) => <BookingIdCell {...value} />
		},
		{
			header: 'LOCATION',
			accessor: 'location',
			headerCellProps: {
				width: '128px',
				padding: '8px 8px 8px 0'
			},
			bodyCellProps: {
				padding: '10px 8px 10px 0',
				noBdr: true
			},
			formatter: (value) => <Location {...value} type={MRB_CONSTANTS.UPCOMING_REQUEST_TYPE} />
		},
		{
			header: 'TRAVELLER',
			accessor: 'traveller',
			headerCellProps: {
				width: '146px',
				padding: '8px 8px 8px 0'
			},
			bodyCellProps: {
				padding: '10px 8px 10px 0',
				noBdr: true
			},
			formatter: (value) => (
				<GenericSpan>
					<GenericP fsize='14px' $lHeight='20px' color='#000'>{value?.name}</GenericP>
					{value?.email && <GenericP fsize='12px' $lHeight='16px' color='#4a4a4a'>{value.email}</GenericP>}
				</GenericSpan>
			)
		},
		{
			header: 'DATE OF BOOKING',
			accessor: 'bookingDateData',
			key: 'BOOKING_DATE',
			isSort: true,
			headerCellProps: {
				width: '100px',
				padding: '8px 8px 8px 0'
			},
			bodyCellProps: {
				padding: '10px 8px 10px 0',
				noBdr: true
			},
			formatter: (value) => <DateData {...value} />
		},
		{
			header: 'DATE OF TRAVEL',
			accessor: 'travelDateData',
			key: 'DATE_OF_TRAVEL',
			isSort: true,
			headerCellProps: {
				width: '101px',
				padding: '8px 8px 8px 0'
			},
			bodyCellProps: {
				padding: '10px 8px 10px 0',
				noBdr: true
			},
			formatter: (value) => <DateData {...value} />
		},
		{
			header: 'STATUS',
			accessor: 'status',
			headerCellProps: {
				width: '110px',
				padding: '8px 8px 8px 0'
			},
			bodyCellProps: {
				padding: '10px 8px 10px 0',
				noBdr: true
			},
			formatter: (value) => value
		},
		{
			header: 'ACTIONS',
			key: 'actions',
			accessor: 'action',
			headerCellProps: {
				width: '100px',
				padding: '8px 0'
			},
			bodyCellProps: {
				padding: '10px 0',
				noBdr: true
			},
			formatter: (value) => (value === false ? null : <Action {...value} type={MRB_CONSTANTS.UPCOMING_REQUEST_TYPE} />)
		}
	]
};

export const SEARCH_KEYS = {
	SEARCH_QUERY: 'searchQuery',
	REQUISITION_ID: 'requisitionId'
};

export const getSearchKey = (requestType) => (
	requestType === MRB_CONSTANTS.UPCOMING_REQUEST_TYPE ?
		SEARCH_KEYS.SEARCH_QUERY :
		SEARCH_KEYS.REQUISITION_ID
);

export const searchInput = (type) => ({
	value: '',
	label: '',
	info: '',
	placeholder: type === MRB_CONSTANTS.UPCOMING_REQUEST_TYPE ? 'Search by Booking ID' : 'Search by Request ID',
	type: 'text',
	textType: 'search'
});

export const errorHeading = 'No Requests Found.';
export const errorContent = 'Try removing some filters to see results.';

export const ACTION_KEYS = {
	VIEW_DETAILS: 'VIEW_DETAILS',
	MODIFY: 'MODIFY',
	DOWNLOAD: 'DOWNLOAD'
};

export const ACTION_BUTTON_CONFIG = {
	[MRB_CONSTANTS.PENDING_REQUEST_TYPE]: [
		{ key: ACTION_KEYS.VIEW_DETAILS, icon: 'viewDetails', label: 'View Details', accessType: ACCESS_TYPES.WRITE }
	],
	[MRB_CONSTANTS.UPCOMING_REQUEST_TYPE]: [
		{ key: ACTION_KEYS.VIEW_DETAILS, icon: 'viewDetails', label: 'View Details', accessType: ACCESS_TYPES.WRITE },
		{ key: ACTION_KEYS.MODIFY, icon: 'modify', label: 'Modify Booking', accessType: ACCESS_TYPES.WRITE },
		{ key: ACTION_KEYS.DOWNLOAD, icon: 'download', label: 'Download' }
	]
};

export const EXPIRED_REQUEST_LABEL = 'Request Expired';
